/**
 * Admin API — contracts #24/#25 (docs/05-dev-spec.md §4, task T.C5 / И8).
 *
 *   #24 GET   /api/audit             → `{ data: { items: [AuditDto] }, meta }` (ADMIN)
 *   #25 GET   /api/users             → `{ data: { users: [UserDto] } }`       (ADMIN)
 *   #25 PATCH /api/users/:id/role    → `{ data: UserDto }`                    (ADMIN)
 *
 * ADDITIVE wave 9 (v1.8.0, T.Y9) — same module, the users contract grew three ways:
 *   #25.1 POST  /api/users           → `{ data: UserDto }` (201)              (ADMIN)
 *   #25.2 PATCH /api/users/:id/profile → body `{displayName?, email?}`        (ADMIN)
 *   #25.7 GET   /api/users/:id/venues → `{ data: { venues, curatorVenueIds,
 *                                 curatorVenues (v1.9.31), department } }`
 * The manager-status dictionary and the partner pool live in their own modules
 * (`./manager-statuses`, `./partners`) — new contract lines, new files, so this
 * one stays the users/audit client.
 *
 * CONTRACT-FIRST: T.B5 (the admin module) lands in parallel, so the DTOs below
 * are mirrored 1:1 from the generated `packages/shared/src/openapi/api-types.ts`
 * (`AuditDto`, `PaginatedAudit`, `UserDto`, `UserListEnvelope`,
 * `UserRolePatchDto`). The list reads stay RAW (`fetchAudit`/`fetchUsers`
 * return `unknown`), because unwrapping `{data:{items|users:[…]}}` vs a bare
 * array is pure logic and belongs in `pages/settings/settings-model.ts` — this
 * module value-imports `client.ts` (`import.meta.env`), which the plain-Node
 * `unit` project cannot load. The role PATCH unwraps in place: it is a mutation,
 * nothing unit-tests its body.
 *
 * Error semantics the page surfaces verbatim (the api's own rules):
 *   #24/#25 below ADMIN      → 403 FORBIDDEN (the UI hides the tabs instead)
 *   #25 demoting the LAST ADMIN → 409 CONFLICT («no ADMIN left in the system»)
 *   #25 unknown user         → 404 NOT_FOUND
 *   #24 unknown `entity`/`userId` are FILTERS, not resources — never 404.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest, buildQuery, requestEnvelope } from './client';
import type { Role } from '../auth/roles';
import type { ManagerDepartment } from '../departments';

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/** `AuditDto` — 1:1 with `AuditLog` (docs/05 §3), the security trail. */
export interface AuditDto {
  id: number;
  /** Null for an action the api performed itself (a worker, an unauthenticated hop). */
  userId: number | null;
  /** LOGIN | EXPORT | ENTITY_CHANGE | … (open string, not an enum). */
  action: string;
  /** application | venue | quota | user | export | … */
  entity: string;
  entityId: string | null;
  /** Free-form context (format, filters, rowCount, oldValue/newValue …). */
  meta: Record<string, unknown> | null;
  /** ISO-8601. */
  createdAt: string;
}

/** The query #24 accepts — all three are server-side. */
export interface AuditListQuery {
  entity?: string;
  userId?: number;
  /** 1-based. */
  page?: number;
  /** Default 50, max 200 on the api side. */
  pageSize?: number;
}

/** `UserDto` — the frozen field set of #25 (`yandexUid` is never exposed). */
export interface UserDto {
  id: number;
  email: string;
  displayName: string | null;
  role: Role;
  /** ISO-8601, null = never signed in. */
  lastLoginAt: string | null;
  /**
   * ADDITIVE (v1.7, T.X4) — `count(venue_managers)`. Absent on a payload an older
   * api sent: `undefined` renders as «0», never as an error.
   */
  managedVenueCount?: number;
  /** ADDITIVE (v1.7, T.X4) — the admin block. Absent (old api) reads as `false`. */
  blocked?: boolean;
  // ── ADDITIVE wave 9 (v1.8.0, T.Y9) — the two external-account fields ─────────
  /**
   * Where the account came from: `YANDEX` (a login the person already had) or
   * `EMAIL_CODE` (an account ADMIN created, signed into with the e-mail code).
   * It never flips: a code login into a YANDEX row changes neither provider nor uid.
   *
   * A13 (wave 10, T.Z10; finished by T.Z22) — the UI renders NOTHING from this
   * field: since D21 every account signs in BOTH ways, so a «способ входа» badge
   * said nothing true about the person. The field stays mapped for the contract
   * mirror only; do not display it.
   */
  authProvider?: AuthProvider;
  /** `count(venue_curators)` — the mirror of `managedVenueCount` for a curator. Absent reads as 0. */
  curatorVenueCount?: number;
  /**
   * ADDITIVE (v1.8.7, T.Z42) — the department of a МЕНЕДЖЕР row: `OP` = Отдел
   * привлечения, `OS` = Отдел сопровождения, `null`/absent = no department (a
   * КУРАТОР, an АДМИН, or an unfiled manager). Absent (an older api) reads as `null`
   * — the filters never pretend to know what the api did not say.
   */
  department?: ManagerDepartment | null;
  /**
   * ADDITIVE (v1.9.53, T.Z177) — `count(audit_logs where user_id = this row)`, the
   * «Действий в журнале» column of the users tab. The #25 LIST read always carries
   * it; absent (an older api) reads as 0. The single-row write answers do NOT carry
   * it — the roster is refetched after every write, so nothing is lost.
   */
  auditActions?: number;
}

/** ADDITIVE (v1.8.0) — the sign-in provider of an `app_users` row (`app_users.auth_provider`). */
export type AuthProvider = 'YANDEX' | 'EMAIL_CODE';

/**
 * ADDITIVE (#25.7, T.X4) — the drawer's venue read also echoes the row's department
 * (v1.8.7, T.Z42), so the «Отдел» editor works from THIS read without a second #25.
 */
export interface UserVenuesDto {
  venues: UserVenueDto[];
  /** The venue ids this user curates, ascending. */
  curatorVenueIds: number[];
  /**
   * ADDITIVE (v1.9.31, T.Z138) — the same links WITH their names, read straight off the
   * api, so the drawer labels them without the venue directory. Absent (an older api)
   * = the directory stays the fallback; a dangling link id is omitted here while the
   * id stays in `curatorVenueIds`.
   */
  curatorVenues?: Array<{ id: number; name: string }>;
  /** ADDITIVE (v1.8.7, T.Z42) — the row's own department; absent (old api) reads as `null`. */
  department?: ManagerDepartment | null;
}

/** ADDITIVE (#25.7, T.X4) — one venue of a user's set, with the name for the drawer's row. */
export interface UserVenueDto {
  venueId: number;
  venueName: string;
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

export const AUDIT_PATH = '/audit';
export const USERS_PATH = '/users';
export const AUDIT_PAGE_SIZE = 25;

export function auditPath(query: AuditListQuery = {}): string {
  return `${AUDIT_PATH}${buildQuery(query)}`;
}

export function usersRolePatchPath(userId: number): string {
  return `/users/${encodeURIComponent(userId)}/role`;
}

/** ADDITIVE (#25.7, T.X4) — the user-side half of `venue_managers`. */
export function userVenuesPath(userId: number): string {
  return `/users/${encodeURIComponent(userId)}/venues`;
}

export function userBlockedPath(userId: number): string {
  return `/users/${encodeURIComponent(userId)}/blocked`;
}

// ---------------------------------------------------------------------------
// ADDITIVE wave 9 (T.Y9) — #25.1 create an account before its first sign-in
// ---------------------------------------------------------------------------

/**
 * `POST /users` (#25.1, ADMIN, audited) — body. There is NO self-registration
 * (Р21) and NO invitation letter (v2.1): the account exists before the person
 * ever signs in, and the way in is the e-mail code — which only works once the
 * row is linked to a venue, i.e. once `curatorVenueIds` is non-empty.
 */
export interface UserCreateInput {
  /** Stored lowercased; a duplicate (normalized) address is a 409. */
  email: string;
  /** What the roster shows until the first sign-in refines it. */
  displayName: string;
  /** Defaults to VIEWER on the api — a curator is an ordinary user with another way in. */
  role?: Role;
  /** The manager links to materialise with the row. */
  venueIds?: number[];
  /** The curator links — create-time convenience; they define the cabinet scope (the code-login gate is the account row since D22). */
  curatorVenueIds?: number[];
  /**
   * ADDITIVE (v1.8.7, T.Z42) — the department to file the account under. A
   * МЕНЕДЖЕР-only fact: the api 400s a КУРАТОР or an АДМИН created with one.
   */
  department?: ManagerDepartment | null;
}

/** Body of the WIDENED `PATCH /users/:id/profile` (#25.2) — at least one key, else the api 400s. */
export interface UserProfilePatchInput {
  displayName?: string | null;
  /**
   * ADDITIVE (v1.8.0, #25.2) — the ADMIN-set address. 409 when taken; an address a
   * YANDEX row holds is refused (a provider is never re-pointed). Re-linking the
   * renamed row is what restores its code login / cabinet scope: the login gate itself
   * is the account row (D22), the links define where the session may go (D21).
   */
  email?: string;
  /**
   * ADDITIVE (v1.8.7, T.Z42) — PATCH semantics: absent = leave it, `null` = clear it,
   * a code = set it. The api refuses (400) a non-МЕНЕДЖЕР target.
   */
  department?: ManagerDepartment | null;
}

/** #24, RAW — `pages/settings/settings-model.ts#parseAuditPage` unwraps it. */
export async function fetchAudit(query: AuditListQuery, signal?: AbortSignal): Promise<unknown> {
  return apiRequest<unknown>('GET', auditPath(query), { signal });
}

/**
 * ADDITIVE (v1.9.53, T.Z177) — the #25 sort, the register's own `sortBy`/`order`
 * pair narrowed to the ONE value the users tab sorts on today. `null` sends NO
 * parameters and gets the api's frozen roster order (highest role first, then
 * e-mail) byte for byte.
 */
export interface UserListSort {
  sortBy: 'auditActions';
  order: 'asc' | 'desc';
}

/** `null` = unsorted (the frozen roster order), never an empty object. */
export type UserSortState = UserListSort | null;

export function usersListPath(sort: UserSortState): string {
  return `${USERS_PATH}${buildQuery(sort ?? {})}`;
}

/** #25 (list), RAW — `pages/settings/settings-model.ts#parseUsers` unwraps it. */
export async function fetchUsers(sort: UserSortState = null, signal?: AbortSignal): Promise<unknown> {
  return apiRequest<unknown>('GET', usersListPath(sort), { signal });
}

/**
 * #25 (role change) — `{ role }` is the whole body, the api rejects any other
 * key with 400 VALIDATION. The updated `UserDto` comes back so the table can
 * swap the row without waiting for the refetch.
 */
export async function patchUserRole(userId: number, role: Role, signal?: AbortSignal): Promise<UserDto> {
  const envelope = await requestEnvelope<UserDto>('PATCH', usersRolePatchPath(userId), ['data'], {
    // `method` is set in BOTH places on purpose: `apiRequest` spreads `init`
    // into the fetch call and never forwards its own `method` argument (see
    // `quotas.ts` for the same note).
    method: 'PATCH',
    signal,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role } satisfies UserRolePatchInput),
  });
  return envelope.data;
}

/** Body of #25 — exactly this one key. */
export interface UserRolePatchInput {
  role: Role;
}

// ---------------------------------------------------------------------------
// ADDITIVE #25.7 (T.X4) — the user drawer's three writes + the one read
// ---------------------------------------------------------------------------

const JSON_HEADERS = { 'Content-Type': 'application/json' };

/**
 * `GET /users/:id/venues` — `{data:{venues:[UserVenueDto], curatorVenueIds:[…],
 * curatorVenues:[{id,name}], department}}`. Wave 9 (T.Y9) widened the read with the
 * curator links; v1.8.7 (T.Z42) added the row's own `department`; v1.9.31 (T.Z138)
 * added the named curator venues; a payload an older api sent degrades to empty
 * lists and `undefined`, never to a crash.
 */
export async function fetchUserVenues(userId: number, signal?: AbortSignal): Promise<UserVenuesDto> {
  const envelope = await requestEnvelope<{
    venues?: UserVenueDto[];
    curatorVenueIds?: number[];
    curatorVenues?: Array<{ id: number; name: string }>;
    department?: ManagerDepartment | null;
  }>('GET', userVenuesPath(userId), ['data'], { signal });
  return {
    venues: envelope.data?.venues ?? [],
    curatorVenueIds: Array.isArray(envelope.data?.curatorVenueIds) ? (envelope.data?.curatorVenueIds as number[]) : [],
    curatorVenues: Array.isArray(envelope.data?.curatorVenues)
      ? (envelope.data?.curatorVenues as Array<{ id: number; name: string }>)
      : undefined,
    department: envelope.data?.department ?? null,
  };
}

/**
 * `PUT /users/:id/venues` — the REPLACE-SET, the mirror of #8 `managerUserIds`:
 * every id in the array is linked, every linked id missing from it is unlinked,
 * `[]` clears the user. The answered set is the truth the drawer keeps.
 */
export async function putUserVenues(userId: number, venueIds: number[], signal?: AbortSignal): Promise<number[]> {
  const envelope = await requestEnvelope<{ managedVenueIds?: number[] }>('PUT', userVenuesPath(userId), ['data'], {
    method: 'PUT',
    signal,
    headers: JSON_HEADERS,
    body: JSON.stringify({ venueIds }),
  });
  return envelope.data?.managedVenueIds ?? [];
}

/** `PATCH /users/:id/blocked` — the block flag; the api answers the updated `UserDto`. */
export async function patchUserBlocked(userId: number, blocked: boolean, signal?: AbortSignal): Promise<UserDto> {
  const envelope = await requestEnvelope<UserDto>('PATCH', userBlockedPath(userId), ['data'], {
    method: 'PATCH',
    signal,
    headers: JSON_HEADERS,
    body: JSON.stringify({ blocked }),
  });
  return envelope.data;
}

/**
 * ADDITIVE (#25.7, T.X4; WIDENED v1.8.0 #25.2, T.Y9) — `PATCH /users/:id/profile`.
 * `displayName` is an ALIAS (the api marks `display_name_custom`, so the next Yandex
 * login keeps it); `null` clears the alias and hands the name back to Yandex. `email`
 * moves the address the person signs in with — the drawer warns about that in RU.
 */
export async function patchUserProfile(
  userId: number,
  patch: UserProfilePatchInput,
  signal?: AbortSignal,
): Promise<UserDto> {
  const envelope = await requestEnvelope<UserDto>('PATCH', userProfilePath(userId), ['data'], {
    method: 'PATCH',
    signal,
    headers: JSON_HEADERS,
    body: JSON.stringify(patch),
  });
  return envelope.data;
}

export function userProfilePath(userId: number): string {
  return `/users/${encodeURIComponent(userId)}/profile`;
}

/**
 * ADDITIVE (#25.1, v1.8.0, T.Y9) — `POST /users`, the account created before its
 * first sign-in. The api answers `201 {data: UserDto}` (200 replayed), 409 on a
 * duplicate normalized address, 400 on an unknown venue id in either id list.
 */
export async function createUser(input: UserCreateInput, signal?: AbortSignal): Promise<UserDto> {
  const envelope = await requestEnvelope<UserDto>('POST', USERS_PATH, ['data'], {
    method: 'POST',
    signal,
    headers: JSON_HEADERS,
    body: JSON.stringify(input satisfies UserCreateInput),
  });
  return envelope.data;
}

// ---------------------------------------------------------------------------
// Cache identity + hooks
// ---------------------------------------------------------------------------

const USERS_ROOT = ['admin', 'users'] as const;
const AUDIT_ROOT = ['admin', 'audit'] as const;

export function auditQueryKey(query: AuditListQuery): readonly [string, string, AuditListQuery] {
  return ['admin', 'audit', query];
}

/**
 * #25 — the users table. A role change anywhere re-reads it.
 *
 * ADDITIVE (v1.9.53, T.Z177) — the sort is part of the cache key (the audit tab's
 * filter rule: a sort change is a fresh read, never a client-side slice), and the
 * key stays a PREFIX of `[...USERS_ROOT, sort]`, so the write hooks' blanket
 * `invalidateQueries({ queryKey: USERS_ROOT })` still reaches every sort variant.
 */
export function useUsers(sort: UserSortState = null) {
  return useQuery({
    queryKey: [...USERS_ROOT, sort],
    queryFn: ({ signal }) => fetchUsers(sort, signal),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    retry: 0,
  });
}

/**
 * #24 — one page of the audit trail. The filters are part of the cache key, so
 * a filter change is a fresh read, never a client-side slice.
 */
export function useAudit(query: AuditListQuery) {
  return useQuery({
    queryKey: auditQueryKey(query),
    queryFn: ({ signal }) => fetchAudit(query, signal),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    retry: 0,
  });
}

/**
 * #25 — the role change. The server writes the audit row in the same
 * transaction, so BOTH invalidations are needed: the table for the new role and
 * the journal for the new `ENTITY_CHANGE` row.
 */
export function useUserRolePatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { userId: number; role: Role }) => patchUserRole(input.userId, input.role),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: USERS_ROOT });
      void queryClient.invalidateQueries({ queryKey: AUDIT_ROOT });
    },
  });
}

// ---------------------------------------------------------------------------
// Cache hooks of the ADDITIVE surface (#25.7, T.X4)
// ---------------------------------------------------------------------------

/** One query per user id: the drawer's venue list, enabled only while it is open. */
export function useUserVenues(userId: number | null) {
  return useQuery({
    queryKey: ['admin', 'users', userId, 'venues'],
    queryFn: ({ signal }) => fetchUserVenues(userId as number, signal),
    enabled: userId !== null,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
}

function useInvalidateUsers(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: USERS_ROOT });
    void queryClient.invalidateQueries({ queryKey: AUDIT_ROOT });
  };
}

/**
 * The venue-set write. The server also writes the audit row, so BOTH roots are
 * invalidated — the roster for the new «Площадок» count and the journal for the
 * new `ENTITY_CHANGE` line.
 *
 * T.Z22 — the user drawer no longer calls this: its link editor writes the SAME
 * diff per venue through #8 (`managerUserIds`/`curatorUserIds`), which is the
 * only write that can also move curator links and keeps each venue's other
 * people intact. The wrapper stays because #25.7 is still in the contract.
 */
export function useUserVenuesSet() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (input: { userId: number; venueIds: number[] }) => putUserVenues(input.userId, input.venueIds),
    onSettled: invalidate,
  });
}

/** The block write — same two invalidations: the roster's `blocked` and the trail. */
export function useUserBlockedPatch() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (input: { userId: number; blocked: boolean }) => patchUserBlocked(input.userId, input.blocked),
    onSettled: invalidate,
  });
}

/** The display-name write (an alias the next login keeps — see the fetcher's note). */
export function useUserProfilePatch() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (input: { userId: number; patch: UserProfilePatchInput }) =>
      patchUserProfile(input.userId, input.patch),
    onSettled: invalidate,
  });
}

// ---------------------------------------------------------------------------
// ADDITIVE wave 9 (T.Y9) — #25.1 create; the roster refreshes itself after it
// ---------------------------------------------------------------------------

/**
 * The create write. The same invalidations as every other users-tab write: the
 * roster grows a row (and the audit journal a `USER_CREATED` line), and a drawer
 * that stays open must not keep showing a roster without the new account.
 */
export function useCreateUser() {
  const invalidate = useInvalidateUsers();
  return useMutation({
    mutationFn: (input: UserCreateInput) => createUser(input),
    onSettled: invalidate,
  });
}
